/**
 * PDF Upload Step
 * Step 2: Upload and process medical record PDFs
 */

import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useFlashMessagesContext } from '../../contexts/FlashMessagesContext';
import type { PDFUploadState } from '../../hooks/useInsuranceReport';
import type { OutputFormat } from '../../types/insurance';
import PDFUploadItem from './PDFUploadItem';

interface PDFUploadStepProps {
  uploadedPDFs: PDFUploadState[];
  onUploadPDF: (file: File) => Promise<void>;
  onFinalize: () => Promise<void>;
  canProceedToFinalize: boolean;
  isProcessing: boolean;
  outputFormat: OutputFormat;
  onOutputFormatChange: (format: OutputFormat) => void;
}

const PDFUploadStep: React.FC<PDFUploadStepProps> = ({
  uploadedPDFs,
  onUploadPDF,
  onFinalize,
  canProceedToFinalize,
  isProcessing,
  outputFormat,
  onOutputFormatChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const { addMessage } = useFlashMessagesContext();

  const validatePDF = (file: File): boolean => {
    if (file.type !== 'application/pdf') {
      addMessage({
        id: crypto.randomUUID(),
        type: 'error',
        title: 'Invalid File Type',
        message: `${file.name} is not a PDF file`,
      });
      return false;
    }

    if (file.size > 20 * 1024 * 1024) {
      addMessage({
        id: crypto.randomUUID(),
        type: 'error',
        title: 'File Too Large',
        message: `${file.name} exceeds 20MB limit`,
      });
      return false;
    }

    return true;
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (validatePDF(file)) {
        validFiles.push(file);
      }
    }

    // Upload all valid files (they will process in parallel)
    for (const file of validFiles) {
      try {
        await onUploadPDF(file);
      } catch (error) {
        console.error('Upload failed for:', file.name, error);
        // Error is handled by the hook
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFinalize = async () => {
    try {
      await onFinalize();
    } catch (error) {
      console.error('Finalize failed:', error);
      // Error is handled by the hook
    }
  };

  const handleFormatChange = (e: SelectChangeEvent<OutputFormat>) => {
    onOutputFormatChange(e.target.value as OutputFormat);
  };

  return (
    <Stack spacing={3}>
      {/* Report Format selector */}
      <FormControl size="small" sx={{ maxWidth: 300 }}>
        <InputLabel id="output-format-label">Report Format</InputLabel>
        <Select
          labelId="output-format-label"
          value={outputFormat}
          label="Report Format"
          onChange={handleFormatChange}
          disabled={uploadedPDFs.length > 0}
        >
          <MenuItem value="standard">Standard</MenuItem>
          <MenuItem value="trupanion">Trupanion</MenuItem>
        </Select>
      </FormControl>

      {/* Drag-drop upload zone */}
      <Paper
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClickUpload}
        sx={{
          p: 4,
          textAlign: 'center',
          border: isDragOver ? '2px dashed primary.main' : '2px dashed #ccc',
          backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Drag and drop PDFs here, or click to select
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload one or more veterinary medical record PDFs (max 20MB each)
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          style={{ display: 'none' }}
        />
      </Paper>

      {/* Uploaded PDFs list */}
      {uploadedPDFs.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Uploaded PDFs ({uploadedPDFs.length})
          </Typography>
          <List>
            {uploadedPDFs.map((pdf, index) => (
              <PDFUploadItem key={index} pdf={pdf} />
            ))}
          </List>
        </Box>
      )}

      {/* Instructions */}
      {uploadedPDFs.length === 0 && (
        <Alert severity="info">
          Upload all medical record PDFs for this pet. You can upload multiple files at once. Once
          all PDFs are uploaded and processed, click "Generate Summary" to continue.
        </Alert>
      )}

      {/* Generate Summary button */}
      {uploadedPDFs.length > 0 && (
        <Button
          variant="contained"
          size="large"
          onClick={handleFinalize}
          disabled={!canProceedToFinalize || isProcessing}
        >
          {isProcessing ? 'Processing PDFs...' : 'Generate Summary'}
        </Button>
      )}

      {isProcessing && (
        <Alert severity="info">PDFs are still processing. Please wait...</Alert>
      )}
    </Stack>
  );
};

export default PDFUploadStep;
